import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChefHat, Package, ShoppingCart, Sparkles, Clock, Users, Flame, 
  Zap, Filter, Brain, Wheat, ArrowRight, ArrowLeft,
  Timer, Target, Leaf, Heart, Cookie, DollarSign, Settings, Shield, Apple, MapPin
} from 'lucide-react';
import { Button } from './button';
import { useNoshStore } from '../../lib/store';
import { useLeftEdgeSwipeDisable } from '../../hooks/useLeftEdgeSwipeDisable';
import { isPWAStandalone } from '../../lib/utils';

interface MealGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (options: MealGenerationOptions) => void;
  onBack?: () => void;
  inventoryCount?: number;
  shoppingItemsCount?: number;
  detectedIngredients?: string[];
}

export interface MealGenerationOptions {
  source: 'inventory' | 'mixed';
  includeShoppingItems: boolean;
  maxCookTime: number;
  servings: number;
  difficulty: 'any' | 'easy' | 'medium' | 'hard';
  mealType: 'any' | 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert';
  prioritizeExpiring: boolean;
  additionalProtein: boolean;
  additionalVegetables: boolean;
  cuisineStyle: 'any' | 'italian' | 'asian' | 'mexican' | 'mediterranean' | 'indian' | 'american';
  spiceLevel: 'none' | 'mild' | 'medium' | 'spicy';
  healthyFocus: boolean;
  budgetFriendly: boolean;
}

// New cooking style type for the UI
type CookingStyle = 'quick' | 'normal' | 'restaurant';

export const MealGenerationModal: React.FC<MealGenerationModalProps> = ({ 
  isOpen, 
  onClose, 
  onGenerate,
  onBack,
  inventoryCount = 0,
  shoppingItemsCount = 0,
  detectedIngredients = []
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(true);
  const { 
    getMealGenerationPreferences, 
    saveMealGenerationPreferences
  } = useNoshStore();

  // Prevent left edge swipe on modal
  useLeftEdgeSwipeDisable({
    pageId: 'MealGenerationModal',
    enabled: isOpen,
    edgeWidth: 50
  });
  
  // New cooking style state for UI
  const [selectedCookingStyle, setSelectedCookingStyle] = useState<CookingStyle>('normal');
  
  // Enhanced options state
  const [options, setOptions] = useState<MealGenerationOptions>({
    source: 'inventory',
    includeShoppingItems: false,
    maxCookTime: 40, // Default to normal meal
    servings: 1,
    difficulty: 'medium', // Default to normal meal
    mealType: 'any',
    prioritizeExpiring: false,
    additionalProtein: false,
    additionalVegetables: false,
    cuisineStyle: 'any',
    spiceLevel: 'medium',
    healthyFocus: false,
    budgetFriendly: false
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load user preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUserPreferences();
    }
  }, [isOpen]);

  // Check if this is photo-to-recipe flow
  const isPhotoRecipe = detectedIngredients && detectedIngredients.length > 0;
  
  // CRITICAL DEBUG: Log photo recipe detection for debugging
  useEffect(() => {
    if (isOpen) {
      console.log('🚨 [MealGenerationModal] CRITICAL DEBUG - Modal opened with:', {
        isPhotoRecipe,
        detectedIngredientsCount: detectedIngredients?.length || 0,
        detectedIngredients: detectedIngredients,
        detectedIngredientsType: typeof detectedIngredients,
        isArray: Array.isArray(detectedIngredients),
        modalOpen: isOpen
      });
      
      // Additional debugging for the conditional rendering
      console.log('🚨 [MealGenerationModal] Conditional check result:', {
        'detectedIngredients.length > 0': detectedIngredients.length > 0,
        'will show section': detectedIngredients.length > 0
      });
    }
  }, [isOpen, isPhotoRecipe, detectedIngredients]);

  const loadUserPreferences = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 [MealGenerationModal] Loading user meal generation preferences...');
      
      // CRITICAL FIX: Check the current ingredient source selection
      const currentIngredientSource = useNoshStore.getState().ingredientSource.selectedSource;
      console.log('🛒 [MealGenerationModal] Current ingredient source selection:', currentIngredientSource);
      
      const savedPrefs = await getMealGenerationPreferences();
      
      if (savedPrefs) {
        console.log('✅ [MealGenerationModal] Found saved preferences, applying them:', savedPrefs);
        
        // Map saved preferences to cooking style first
        const cookingStyle = mapOptionsToCookingStyle(savedPrefs.maxCookTime, savedPrefs.preferredDifficulty);
        setSelectedCookingStyle(cookingStyle);
        
        // Get the correct maxCookTime and difficulty based on the cooking style
        let correctMaxCookTime = savedPrefs.maxCookTime;
        let correctDifficulty = savedPrefs.preferredDifficulty;
        
        switch (cookingStyle) {
          case 'quick':
            correctMaxCookTime = 20;
            correctDifficulty = 'easy';
            break;
          case 'normal':
            correctMaxCookTime = 40;
            correctDifficulty = 'medium';
            break;
          case 'restaurant':
            correctMaxCookTime = 120;
            correctDifficulty = 'hard';
            break;
        }
        
        // CRITICAL FIX: Override source and includeShoppingItems based on current selection
        let sourceMode: 'inventory' | 'mixed' = savedPrefs.defaultSource;
        let includeShoppingItems = savedPrefs.includeShoppingItems;
        
        if (currentIngredientSource === 'mixed') {
          sourceMode = 'mixed';
          includeShoppingItems = true;
          console.log('🛒 [MealGenerationModal] OVERRIDE: User selected mixed source, setting includeShoppingItems=true');
        } else if (currentIngredientSource === 'inventory') {
          sourceMode = 'inventory';
          includeShoppingItems = false;
          console.log('🏪 [MealGenerationModal] OVERRIDE: User selected inventory source, setting includeShoppingItems=false');
        }
        
        const newOptions = {
          source: sourceMode,
          includeShoppingItems: includeShoppingItems,
          maxCookTime: correctMaxCookTime, // Use corrected value
          servings: savedPrefs.preferredServings,
          difficulty: correctDifficulty, // Use corrected value
          mealType: savedPrefs.preferredMealType,
          prioritizeExpiring: isPhotoRecipe ? false : savedPrefs.prioritizeExpiring, // Off by default for photo recipes
          additionalProtein: savedPrefs.additionalProtein,
          additionalVegetables: savedPrefs.additionalVegetables,
          cuisineStyle: savedPrefs.preferredCuisineStyle,
          spiceLevel: savedPrefs.preferredSpiceLevel,
          healthyFocus: savedPrefs.healthyFocus,
          budgetFriendly: savedPrefs.budgetFriendly,
        };
        
        setOptions(newOptions);
        
        console.log(`🔧 [MealGenerationModal] Final options applied:`, newOptions);
      } else {
        console.log('ℹ️ [MealGenerationModal] No saved preferences found, using defaults');
        
        // CRITICAL FIX: Even with defaults, respect the ingredient source selection
        let includeShoppingItems = false;
        let sourceMode: 'inventory' | 'mixed' = 'inventory';
        
        if (currentIngredientSource === 'mixed') {
          sourceMode = 'mixed';
          includeShoppingItems = true;
          console.log('🛒 [MealGenerationModal] DEFAULT OVERRIDE: User selected mixed source, setting includeShoppingItems=true');
        }
        
        setOptions(prev => ({
          ...prev,
          source: sourceMode,
          includeShoppingItems: includeShoppingItems,
          prioritizeExpiring: isPhotoRecipe ? false : prev.prioritizeExpiring
        }));
      }
    } catch (error) {
      console.error('❌ [MealGenerationModal] Failed to load meal generation preferences:', error);
      // Continue with defaults - not a critical error
    } finally {
      setIsLoading(false);
    }
  };

  // Log options changes after state updates
  useEffect(() => {
    if (!isLoading && isOpen) {
      console.log('📋 [MealGenerationModal] Current options after state update:', options);
    }
  }, [options, isLoading, isOpen]);

  const saveUserPreferences = async (currentOptions: MealGenerationOptions) => {
    try {
      console.log('💾 Saving meal generation preferences:', currentOptions);
      await saveMealGenerationPreferences({
        defaultSource: currentOptions.source,
        includeShoppingItems: currentOptions.includeShoppingItems,
        prioritizeExpiring: currentOptions.prioritizeExpiring,
        maxCookTime: currentOptions.maxCookTime,
        preferredServings: currentOptions.servings,
        preferredDifficulty: currentOptions.difficulty,
        preferredMealType: currentOptions.mealType,
        additionalProtein: currentOptions.additionalProtein,
        additionalVegetables: currentOptions.additionalVegetables,
        preferredCuisineStyle: currentOptions.cuisineStyle,
        preferredSpiceLevel: currentOptions.spiceLevel,
        healthyFocus: currentOptions.healthyFocus,
        budgetFriendly: currentOptions.budgetFriendly,
      });
      console.log('✅ Preferences saved successfully');
    } catch (error) {
      console.error('❌ Failed to save meal generation preferences:', error);
    }
  };

  // Body scroll lock for mobile
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';
      
      if (isMobile) {
        document.body.classList.add('modal-open');
      }
      
      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.touchAction = '';
        document.documentElement.style.overflow = '';
        document.body.classList.remove('modal-open');
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, isMobile]);

  // Reset modal state when it closes (not when it opens)
  useEffect(() => {
    if (!isOpen) {
      // Only reset when modal is actually closed, this will prepare for next opening
      setOptions({
        source: 'inventory', // This will be overridden by loadUserPreferences when modal opens
        includeShoppingItems: false,
        maxCookTime: 40, // Default to normal meal
        servings: 1,
        difficulty: 'medium', // Default to normal meal
        mealType: 'any',
        prioritizeExpiring: false,
        additionalProtein: false,
        additionalVegetables: false,
        cuisineStyle: 'any',
        spiceLevel: 'medium',
        healthyFocus: false,
        budgetFriendly: false
      });
    }
  }, [isOpen]);

  // Debug logging helper
  const logOptionChange = (option: string, value: any) => {
    console.log(`🎯 [MealGenModal] User changed ${option} to:`, value);
  };

  const handleGenerate = async () => {
    console.log('🎯 [MODAL] Generate button clicked!');
    console.log('🚀 [MODAL] User clicked Generate with final options:', JSON.stringify(options, null, 2));
    
    // Save preferences before generating
    await saveUserPreferences(options);
    
    // Close the modal first
    onClose();
    
    // Call the real AI generation function
    console.log('🔄 [MODAL] Calling onGenerate with options...');
    onGenerate(options);
  };

  // Map cooking style to backend options
  const handleCookingStyleChange = (style: CookingStyle) => {
    setSelectedCookingStyle(style);
    
    switch (style) {
      case 'quick':
        setOptions(prev => ({ ...prev, maxCookTime: 20, difficulty: 'easy' }));
        break;
      case 'normal':
        setOptions(prev => ({ ...prev, maxCookTime: 40, difficulty: 'medium' }));
        break;
      case 'restaurant':
        setOptions(prev => ({ ...prev, maxCookTime: 120, difficulty: 'hard' }));
        break;
    }
  };

  // Map backend options to cooking style for UI
  const mapOptionsToCookingStyle = (maxCookTime: number, difficulty: string): CookingStyle => {
    if (maxCookTime <= 20 && difficulty === 'easy') return 'quick';
    if (maxCookTime <= 40 && difficulty === 'medium') return 'normal';
    if (maxCookTime > 40 && difficulty === 'hard') return 'restaurant';
    
    // Default mapping based on time if difficulty doesn't match
    if (maxCookTime <= 25) return 'quick';
    if (maxCookTime <= 45) return 'normal';
    return 'restaurant';
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto">
      {/* Header - Desktop Only */}
      {!isMobile && (
        <div className="space-y-3">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-gray-100">
              AI Meal Generator
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Create personalized recipes with smart ingredient suggestions
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-gray-600 dark:text-gray-400">Loading your preferences...</span>
          </div>
        </div>
      )}

      {/* Main Content - Only show when not loading */}
      {!isLoading && (
        <>


      {/* Detected Ingredients Section - Only show if we have detected ingredients */}
      {detectedIngredients.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Sparkles size={20} className="mr-2 text-purple-500" />
            Detected Ingredients
          </h3>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-2xl p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-800/30 rounded-lg flex items-center justify-center mr-3">
                <Brain size={16} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  AI detected {detectedIngredients.length} ingredients from your photo
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  These will be prioritized in your recipe
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {detectedIngredients.map((ingredient, index) => (
                <div
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full border border-purple-200 dark:border-purple-700 shadow-sm"
                >
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {ingredient}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}



      {/* Core Settings */}
      <div className="space-y-5">
        {/* New Cooking Style Section */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Clock size={18} className="text-blue-500" />
            <label className="font-medium text-gray-900 dark:text-gray-100">
              Cooking Style
            </label>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {[
              { 
                value: 'quick', 
                title: 'Quick Meal', 
                subtitle: 'Fast & simple • 20 minutes or less',
                icon: '⚡', 
                color: 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300',
                hoverColor: 'hover:border-green-400'
              },
              { 
                value: 'normal', 
                title: 'Normal Meal', 
                subtitle: 'Proper cooking • 40 minutes or less',
                icon: '👨‍🍳', 
                color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300',
                hoverColor: 'hover:border-blue-400'
              },
              { 
                value: 'restaurant', 
                title: 'Restaurant Quality', 
                subtitle: 'Masterpiece cooking • Up to 2 hours',
                icon: '🔥', 
                color: 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-700 dark:text-red-300',
                hoverColor: 'hover:border-red-400'
              }
            ].map(style => (
              <button
                key={style.value}
                onClick={() => handleCookingStyleChange(style.value as CookingStyle)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedCookingStyle === style.value
                    ? style.color
                    : `border-gray-200 dark:border-gray-700 ${style.hoverColor}`
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{style.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                      {style.title}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {style.subtitle}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Meal Type */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Timer size={18} className="text-primary" />
            <label className="font-medium text-gray-900 dark:text-gray-100">
              Meal Type
            </label>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              { value: 'any', label: 'Any', icon: '🍽️', color: 'bg-slate-50 dark:bg-slate-900/20', iconBg: 'bg-slate-100 dark:bg-slate-800/30', textColor: 'text-slate-700 dark:text-slate-300' },
              { value: 'breakfast', label: 'Breakfast', icon: '🌅', color: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-800/30', textColor: 'text-amber-700 dark:text-amber-300' },
              { value: 'lunch', label: 'Lunch', icon: '☀️', color: 'bg-yellow-50 dark:bg-yellow-900/20', iconBg: 'bg-yellow-100 dark:bg-yellow-800/30', textColor: 'text-yellow-700 dark:text-yellow-300' },
              { value: 'dinner', label: 'Dinner', icon: '🌙', color: 'bg-indigo-50 dark:bg-indigo-900/20', iconBg: 'bg-indigo-100 dark:bg-indigo-800/30', textColor: 'text-indigo-700 dark:text-indigo-300' },
              { value: 'snack', label: 'Snack', icon: '🍎', color: 'bg-green-50 dark:bg-green-900/20', iconBg: 'bg-green-100 dark:bg-green-800/30', textColor: 'text-green-700 dark:text-green-300' },
              { value: 'dessert', label: 'Dessert', icon: '🍰', color: 'bg-pink-50 dark:bg-pink-900/20', iconBg: 'bg-pink-100 dark:bg-pink-800/30', textColor: 'text-pink-700 dark:text-pink-300' }
            ].map(item => {
              const isSelected = options.mealType === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => {
            logOptionChange('mealType', item.value);
            setOptions(prev => ({ ...prev, mealType: item.value as any }));
          }}
                  className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                    isSelected
                      ? item.color + ' border-transparent shadow-md'
                      : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? item.iconBg : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <span className="text-lg">{item.icon}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-left">
                      <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                        isSelected ? item.textColor : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {item.label}
                      </h4>
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-white border-white shadow-sm' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                      )}
                    </div>
                  </div>
                  
                  {/* Selection indicator background */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Cuisine Style */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Target size={18} className="text-primary" />
            <label className="font-medium text-gray-900 dark:text-gray-100">
              Cuisine Style
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { value: 'any', label: 'Surprise me', icon: '🌍', color: 'bg-slate-50 dark:bg-slate-900/20', iconBg: 'bg-slate-100 dark:bg-slate-800/30', textColor: 'text-slate-700 dark:text-slate-300' },
              { value: 'italian', label: 'Italian', icon: '🍝', color: 'bg-emerald-50 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-800/30', textColor: 'text-emerald-700 dark:text-emerald-300' },
              { value: 'asian', label: 'Asian', icon: '🥢', color: 'bg-red-50 dark:bg-red-900/20', iconBg: 'bg-red-100 dark:bg-red-800/30', textColor: 'text-red-700 dark:text-red-300' },
              { value: 'mexican', label: 'Mexican', icon: '🌮', color: 'bg-orange-50 dark:bg-orange-900/20', iconBg: 'bg-orange-100 dark:bg-orange-800/30', textColor: 'text-orange-700 dark:text-orange-300' },
              { value: 'mediterranean', label: 'Mediterranean', icon: '🫒', color: 'bg-teal-50 dark:bg-teal-900/20', iconBg: 'bg-teal-100 dark:bg-teal-800/30', textColor: 'text-teal-700 dark:text-teal-300' },
              { value: 'indian', label: 'Indian', icon: '🍛', color: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-800/30', textColor: 'text-amber-700 dark:text-amber-300' },
              { value: 'american', label: 'American', icon: '🍔', color: 'bg-blue-50 dark:bg-blue-900/20', iconBg: 'bg-blue-100 dark:bg-blue-800/30', textColor: 'text-blue-700 dark:text-blue-300' }
            ].map(item => {
              const isSelected = options.cuisineStyle === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => {
              logOptionChange('cuisineStyle', item.value);
              setOptions(prev => ({ ...prev, cuisineStyle: item.value as any }));
            }}
                  className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                    isSelected
                      ? item.color + ' border-transparent shadow-md'
                      : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? item.iconBg : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <span className="text-lg">{item.icon}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-left">
                      <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                        isSelected ? item.textColor : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {item.label}
                      </h4>
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-white border-white shadow-sm' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                      )}
                    </div>
                  </div>
                  
                  {/* Selection indicator background */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Spice Level */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Flame size={18} className="text-red-500" />
            <label className="font-medium text-gray-900 dark:text-gray-100">
              Spice Level
            </label>
          </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: 'none', label: 'None', icon: '⚪', color: 'bg-gray-50 dark:bg-gray-900/20', iconBg: 'bg-gray-100 dark:bg-gray-800/30', textColor: 'text-gray-700 dark:text-gray-300' },
                { value: 'mild', label: 'Mild', icon: '🟢', color: 'bg-green-50 dark:bg-green-900/20', iconBg: 'bg-green-100 dark:bg-green-800/30', textColor: 'text-green-700 dark:text-green-300' },
                { value: 'medium', label: 'Medium', icon: '🟡', color: 'bg-yellow-50 dark:bg-yellow-900/20', iconBg: 'bg-yellow-100 dark:bg-yellow-800/30', textColor: 'text-yellow-700 dark:text-yellow-300' },
                { value: 'spicy', label: 'Spicy', icon: '🔴', color: 'bg-red-50 dark:bg-red-900/20', iconBg: 'bg-red-100 dark:bg-red-800/30', textColor: 'text-red-700 dark:text-red-300' }
              ].map(item => {
                const isSelected = options.spiceLevel === item.value;
                return (
                  <button
                    key={item.value}
                    onClick={() => {
              logOptionChange('spiceLevel', item.value);
              setOptions(prev => ({ ...prev, spiceLevel: item.value as any }));
            }}
                    className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                      isSelected
                        ? item.color + ' border-transparent shadow-md'
                        : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? item.iconBg : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <span className="text-lg">{item.icon}</span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 text-left">
                        <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                          isSelected ? item.textColor : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {item.label}
                        </h4>
                      </div>
                      
                      {/* Checkbox indicator */}
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected 
                          ? 'bg-white border-white shadow-sm' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {isSelected && (
                          <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                        )}
                      </div>
                    </div>
                    
                    {/* Selection indicator background */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                    )}
                  </button>
                );
              })}
            </div>
        </div>

        {/* Special Options - Hidden for photo recipes */}
        {!isPhotoRecipe && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
            <Sparkles size={18} className="mr-2 text-yellow-500" />
            Special Options
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { 
                key: 'prioritizeExpiring',
                label: 'Prioritize Expiring Items', 
                description: 'Use items that expire soon first',
                icon: Zap,
                color: 'bg-amber-50 dark:bg-amber-900/20', 
                iconBg: 'bg-amber-100 dark:bg-amber-800/30', 
                textColor: 'text-amber-700 dark:text-amber-300',
                iconColor: 'text-amber-600'
              },
              { 
                key: 'healthyFocus',
                label: 'Healthy Focus', 
                description: 'Prioritize nutritious, balanced recipes',
                icon: Heart,
                color: 'bg-green-50 dark:bg-green-900/20', 
                iconBg: 'bg-green-100 dark:bg-green-800/30', 
                textColor: 'text-green-700 dark:text-green-300',
                iconColor: 'text-green-600'
              },
              { 
                key: 'budgetFriendly',
                label: 'Budget Friendly', 
                description: 'Focus on affordable, simple ingredients',
                icon: Cookie,
                color: 'bg-blue-50 dark:bg-blue-900/20', 
                iconBg: 'bg-blue-100 dark:bg-blue-800/30', 
                textColor: 'text-blue-700 dark:text-blue-300',
                iconColor: 'text-blue-600'
              }
            ].map(item => {
              const isSelected = options[item.key as keyof MealGenerationOptions] as boolean;
              const IconComponent = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => {
              const newValue = !options[item.key as keyof MealGenerationOptions];
              logOptionChange(item.key, newValue);
              setOptions(prev => ({ ...prev, [item.key]: newValue }));
            }}
                  className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                    isSelected
                      ? item.color + ' border-transparent shadow-md'
                      : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? item.iconBg : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <IconComponent size={20} className={item.iconColor} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-left">
                      <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                        isSelected ? item.textColor : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {item.label}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {item.description}
                      </p>
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-white border-white shadow-sm' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                      )}
                    </div>
                  </div>
                  
                  {/* Selection indicator background */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        )}
      </div>
      </>
      )}

      {/* Generate Button */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          onClick={handleGenerate}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-primary via-primary-hover to-accent hover:from-primary-hover hover:via-accent hover:to-primary text-white font-medium text-base shadow-lg hover:shadow-lg transition-all duration-200"
        >
          <Brain size={18} className="mr-2" />
          Generate Perfect Recipe
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );

  const modalJSX = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "linear" }}
            onClick={onClose}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              zIndex: 9998,
              margin: 0, 
              padding: 0 
            }}
          />

          {/* Modal */}
          {isMobile ? (
            // Mobile: Slide up from bottom with safe area handling
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ 
                duration: 0.3,
                ease: "linear"
              }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                top: 'auto',
                width: '100vw',
                height: '100dvh', // Use dynamic viewport height
                backgroundColor: 'white',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                zIndex: 9999,
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                paddingTop: 'env(safe-area-inset-top)', // Safe area for notch/status bar
                paddingBottom: 'env(safe-area-inset-bottom)' // Safe area for home indicator
              }}
              className="dark:bg-gray-900"
            >
              <div className="h-full flex flex-col">
                {/* Header with X Button Only - Enhanced safe area handling */}
                <div 
                  className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
                  style={{
                    paddingTop: 'max(1rem, env(safe-area-inset-top))', // Ensure minimum padding
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                    paddingBottom: '1rem'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Meal Generator</h1>
                    
                    {/* X Close Button on the right */}
                    <button 
                      onClick={onClose} 
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
                      style={{ minHeight: '44px', minWidth: '44px' }} // Ensure touch target size
                    >
                      <X size={20} className="text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div 
                  className="flex-1 overflow-y-auto p-6"
                  style={{
                    paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' // Safe area for home indicator
                  }}
                >
                  {modalContent}
                </div>
              </div>
            </motion.div>
          ) : (
            // Desktop: Center modal
            <div 
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                margin: 0, 
                padding: '0 1rem' 
              }}
              onClick={onClose}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ 
                  duration: 0.25,
                  ease: "linear"
                }}
                className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8 relative">
                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="absolute top-6 right-6 rounded-full z-10"
                  >
                    <X size={24} />
                  </Button>
                  {modalContent}
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalJSX, document.body);
}; 